import React, { useEffect, useMemo, useState } from 'react';
import { useApp, api, API } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Film, Search, Share2, Trash2, Download, Copy, PlayCircle, Loader2 } from 'lucide-react';

const PROVIDER_LABELS = {
  'fal-ltx': { ar: '⚡ LTX', en: '⚡ LTX' },
  'fal-kling': { ar: '🎬 Kling', en: '🎬 Kling' },
  'sora2': { ar: '🌟 Sora 2', en: '🌟 Sora 2' },
};

function tokenParam() {
  const t = localStorage.getItem('zayed_token');
  return t ? `?auth=${encodeURIComponent(t)}` : '';
}

function videoSrc(video_url) {
  if (!video_url) return '';
  const base = process.env.REACT_APP_BACKEND_URL;
  return `${base}${video_url}${tokenParam()}`;
}

export default function VideoLibraryPage() {
  const { user, lang } = useApp();
  const isTeacherOrAdmin = user && (user.role === 'teacher' || user.role === 'admin');
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [providerFilter, setProviderFilter] = useState('all');
  const [goalFilter, setGoalFilter] = useState('');
  const [player, setPlayer] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (providerFilter !== 'all') params.provider = providerFilter;
    if (goalFilter) params.linked_goal_id = goalFilter;
    api.get('/ai/videos', { params }).then(r => setVideos(r.data || []))
      .catch(e => toast.error(e.response?.data?.detail || (lang === 'ar' ? 'خطأ في الجلب' : 'Fetch error')))
      .finally(() => setLoading(false));
  }, [providerFilter, goalFilter, refreshKey, lang]);

  const filtered = useMemo(() => {
    if (!q.trim()) return videos;
    const s = q.trim().toLowerCase();
    return videos.filter(v =>
      (v.prompt || '').toLowerCase().includes(s) ||
      (v.linked_goal_title || '').toLowerCase().includes(s) ||
      (v.linked_goal_code || '').toLowerCase().includes(s) ||
      (v.teacher_name || '').toLowerCase().includes(s)
    );
  }, [q, videos]);

  const goalGroups = useMemo(() => {
    const map = {};
    for (const v of filtered) {
      const key = v.linked_goal_code || (lang === 'ar' ? 'بدون هدف' : 'No goal');
      if (!map[key]) map[key] = { code: v.linked_goal_code, title: v.linked_goal_title, videos: [] };
      map[key].videos.push(v);
    }
    return Object.entries(map);
  }, [filtered, lang]);

  const toggleShare = async (v, shared) => {
    try {
      await api.post(`/ai/videos/${v.id}/share`, { shared });
      toast.success(shared ? (lang === 'ar' ? 'تمت المشاركة' : 'Shared') : (lang === 'ar' ? 'تم إلغاء المشاركة' : 'Unshared'));
      setRefreshKey(k => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const del = async (v) => {
    if (!window.confirm(lang === 'ar' ? 'حذف الفيديو نهائياً؟' : 'Delete this video?')) return;
    try {
      await api.delete(`/ai/videos/${v.id}`);
      toast.success(lang === 'ar' ? 'تم الحذف' : 'Deleted');
      setRefreshKey(k => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const copyLink = (v) => {
    const link = `${window.location.origin}${v.video_url}${tokenParam()}`;
    navigator.clipboard.writeText(link);
    toast.success(lang === 'ar' ? 'تم نسخ الرابط' : 'Link copied');
  };

  return (
    <main id="main" className="max-w-7xl mx-auto px-4 py-8" data-testid="video-library-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-100 text-rose-700 text-xs font-semibold mb-3">
            <Film className="w-3 h-3" /> {lang === 'ar' ? 'مكتبة الفيديو' : 'Video Library'}
          </div>
          <h1 className="text-3xl sm:text-4xl font-black mb-2">
            {lang === 'ar' ? 'مكتبة الفيديوهات التعليمية' : 'Educational Video Library'}
          </h1>
          <p className="text-muted-foreground max-w-2xl">
            {lang === 'ar'
              ? 'جميع الفيديوهات المُنتَجة بالذكاء الاصطناعي مصنّفة حسب هدف المنهج. شارِكها مع الطلاب وأولياء الأمور بضغطة واحدة.'
              : 'All AI-generated videos grouped by curriculum goal. Share with students and parents in one click.'}
          </p>
        </div>
        {isTeacherOrAdmin && (
          <Button asChild data-testid="generate-new-video-btn" className="bg-primary">
            <a href="/ai-studio">{lang === 'ar' ? '+ توليد فيديو جديد' : '+ Generate new video'}</a>
          </Button>
        )}
      </div>

      <div className="grid sm:grid-cols-[1fr_200px_200px] gap-3 mb-6">
        <div className="relative">
          <Search className="absolute inset-y-0 my-auto start-3 w-4 h-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={lang === 'ar' ? 'ابحث في الوصف أو الهدف…' : 'Search prompt or goal…'}
            className="ps-9"
            data-testid="video-search"
          />
        </div>
        <Select value={providerFilter} onValueChange={setProviderFilter}>
          <SelectTrigger data-testid="provider-filter"><SelectValue placeholder="Provider" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{lang === 'ar' ? 'كل المزوّدين' : 'All providers'}</SelectItem>
            <SelectItem value="fal-ltx">⚡ LTX</SelectItem>
            <SelectItem value="fal-kling">🎬 Kling</SelectItem>
            <SelectItem value="sora2">🌟 Sora 2</SelectItem>
          </SelectContent>
        </Select>
        <Input
          value={goalFilter}
          onChange={(e) => setGoalFilter(e.target.value)}
          placeholder={lang === 'ar' ? 'رمز هدف (مثل GB-0083)' : 'Goal ID / code'}
          data-testid="goal-filter"
        />
      </div>

      {loading ? (
        <div className="text-center py-16 text-muted-foreground">
          <Loader2 className="w-8 h-8 mx-auto animate-spin mb-3" />
          {lang === 'ar' ? 'جارٍ التحميل…' : 'Loading…'}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="empty-videos">
          <Film className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-semibold">
            {lang === 'ar' ? 'لا توجد فيديوهات بعد' : 'No videos yet'}
          </p>
          <p className="text-xs mt-1">
            {lang === 'ar'
              ? 'انتقل إلى استوديو الذكاء الاصطناعي وأنشئ أول فيديو للهدف.'
              : 'Head to the AI Studio and generate your first goal video.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-8">
          {goalGroups.map(([goalKey, group]) => (
            <section key={goalKey} data-testid={`goal-group-${goalKey}`}>
              <div className="flex items-center gap-3 mb-3">
                <Badge variant="outline" className="font-mono">{group.code || (lang === 'ar' ? 'بدون رمز' : 'No code')}</Badge>
                <h2 className="text-lg font-bold line-clamp-1">{group.title || (lang === 'ar' ? 'فيديوهات عامة' : 'General videos')}</h2>
                <span className="text-xs text-muted-foreground">· {group.videos.length}</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {group.videos.map(v => (
                  <VideoCard
                    key={v.id}
                    v={v}
                    lang={lang}
                    isTeacherOrAdmin={isTeacherOrAdmin}
                    isOwner={user && v.user_id === user.id}
                    onPlay={() => setPlayer(v)}
                    onShareToggle={(next) => toggleShare(v, next)}
                    onDelete={() => del(v)}
                    onCopy={() => copyLink(v)}
                    onOpenShare={() => setShareTarget(v)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Player modal */}
      <Dialog open={!!player} onOpenChange={(o) => !o && setPlayer(null)}>
        <DialogContent className="max-w-3xl" data-testid="video-player-dialog">
          <DialogHeader>
            <DialogTitle className="line-clamp-2">
              {player?.linked_goal_title || (lang === 'ar' ? 'مشاهدة الفيديو' : 'Watch video')}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {player?.linked_goal_code} · {(PROVIDER_LABELS[player?.provider] || {})[lang] || player?.provider}
            </DialogDescription>
          </DialogHeader>
          {player && (
            <div className="space-y-3">
              <div className="rounded-xl overflow-hidden bg-black">
                <video
                  src={videoSrc(player.video_url)}
                  controls
                  autoPlay
                  className="w-full max-h-[70vh]"
                  data-testid="video-player"
                />
              </div>
              <details className="text-sm bg-stone-50 p-3 rounded-lg">
                <summary className="cursor-pointer font-semibold">
                  {lang === 'ar' ? 'وصف المطالبة' : 'Prompt'}
                </summary>
                <p className="mt-2 whitespace-pre-wrap text-muted-foreground text-xs">{player.prompt}</p>
              </details>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Share modal */}
      <ShareDialog
        video={shareTarget}
        lang={lang}
        onClose={() => setShareTarget(null)}
        onSaved={() => { setShareTarget(null); setRefreshKey(k => k + 1); }}
      />
    </main>
  );
}

function VideoCard({ v, lang, isTeacherOrAdmin, isOwner, onPlay, onShareToggle, onDelete, onCopy, onOpenShare }) {
  return (
    <Card className="overflow-hidden hover:border-primary transition-colors" data-testid={`video-card-${v.id}`}>
      <button
        onClick={onPlay}
        className="relative w-full aspect-video bg-black grid place-items-center group"
        aria-label={lang === 'ar' ? 'شغّل الفيديو' : 'Play video'}
      >
        <video
          src={videoSrc(v.video_url) + '#t=0.1'}
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100"
          muted
        />
        <PlayCircle className="relative w-14 h-14 text-white/90 drop-shadow-lg group-hover:scale-110 transition-transform" />
      </button>
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="secondary" className="text-[10px]">
            {(PROVIDER_LABELS[v.provider] || {})[lang] || v.provider}
          </Badge>
          <Badge variant="outline" className="text-[10px]">{v.duration || 5}s</Badge>
          {v.shared && <Badge className="text-[10px] bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid={`badge-shared-${v.id}`}>
            <Share2 className="w-3 h-3 me-1" />{lang === 'ar' ? 'مُشارَك' : 'Shared'}
          </Badge>}
        </div>
        <p className="text-xs line-clamp-2 text-muted-foreground">{v.prompt}</p>
        {v.teacher_name && <p className="text-[10px] text-muted-foreground">
          {lang === 'ar' ? 'المعلم' : 'Teacher'}: {v.teacher_name}
        </p>}
        <div className="flex items-center gap-1.5 pt-1 flex-wrap">
          <Button size="sm" variant="outline" onClick={onPlay} data-testid={`play-btn-${v.id}`}>
            <PlayCircle className="w-3.5 h-3.5 me-1" />{lang === 'ar' ? 'تشغيل' : 'Play'}
          </Button>
          {(isTeacherOrAdmin && isOwner) && (
            <>
              <Button
                size="sm"
                variant={v.shared ? 'default' : 'outline'}
                onClick={() => v.shared ? onShareToggle(false) : onOpenShare()}
                data-testid={`share-btn-${v.id}`}
              >
                <Share2 className="w-3.5 h-3.5 me-1" />
                {v.shared ? (lang === 'ar' ? 'إلغاء' : 'Unshare') : (lang === 'ar' ? 'شارك' : 'Share')}
              </Button>
              <Button size="sm" variant="outline" onClick={onCopy} data-testid={`copy-btn-${v.id}`}>
                <Copy className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="outline" asChild data-testid={`download-btn-${v.id}`}>
                <a href={videoSrc(v.video_url)} download={v.file_name || 'video.mp4'}>
                  <Download className="w-3.5 h-3.5" />
                </a>
              </Button>
              <Button size="sm" variant="ghost" className="text-destructive" onClick={onDelete} data-testid={`delete-btn-${v.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

function ShareDialog({ video, lang, onClose, onSaved }) {
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [openGlobal, setOpenGlobal] = useState(true);

  useEffect(() => {
    if (!video) return;
    setSelected(Object.fromEntries((video.shared_with || []).map(id => [id, true])));
    setOpenGlobal(true);
    // Load students (teacher's roster or admin's all students)
    api.get('/students')
      .then(r => setStudents(r.data || []))
      .catch(() => setStudents([]));
  }, [video]);

  if (!video) return null;

  const save = async () => {
    setLoading(true);
    try {
      const student_ids = openGlobal ? [] : Object.entries(selected).filter(([, on]) => on).map(([id]) => id);
      await api.post(`/ai/videos/${video.id}/share`, { shared: true, student_ids });
      toast.success(lang === 'ar' ? 'تمت المشاركة بنجاح' : 'Shared successfully');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!video} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle>{lang === 'ar' ? 'مشاركة الفيديو' : 'Share video'}</DialogTitle>
          <DialogDescription className="text-xs">
            {video.linked_goal_code ? `${video.linked_goal_code} · ` : ''}{video.linked_goal_title}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <div className="font-semibold text-sm">
                {lang === 'ar' ? 'مشاركة مع جميع طلاب الهدف' : 'Share with all goal students'}
              </div>
              <div className="text-[11px] text-muted-foreground">
                {lang === 'ar' ? 'أي طالب هذا الهدف ضمن خطته الفردية سيراه.' : 'Any student whose IEP contains this goal will see it.'}
              </div>
            </div>
            <Switch checked={openGlobal} onCheckedChange={setOpenGlobal} data-testid="share-all-toggle" />
          </div>

          {!openGlobal && (
            <div>
              <div className="text-xs font-semibold mb-2 text-muted-foreground">
                {lang === 'ar' ? 'أو اختر طلاباً محدّدين:' : 'Or select specific students:'}
              </div>
              <div className="max-h-64 overflow-y-auto space-y-1 border rounded-lg p-2">
                {students.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-3">
                    {lang === 'ar' ? 'لا يوجد طلاب' : 'No students'}
                  </p>
                )}
                {students.map(s => (
                  <label key={s.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-stone-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!selected[s.id]}
                      onChange={(e) => setSelected({ ...selected, [s.id]: e.target.checked })}
                      data-testid={`student-cb-${s.id}`}
                    />
                    <span className="text-sm">{s.full_name_ar || s.full_name_en || s.full_name || s.student_email || s.parent_email}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <Button className="w-full" onClick={save} disabled={loading} data-testid="save-share-btn">
            {loading && <Loader2 className="w-4 h-4 me-2 animate-spin" />}
            {lang === 'ar' ? 'حفظ المشاركة' : 'Save share'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
